import { Button, Loader } from '@plaidev/sour-react';
import type { ReactElement } from 'react';
import { DomainV2 } from 'troppus-common/domain';

import ImportTask = DomainV2.Import.ImportTask;

export type LatestImportSummarySectionProps = {
  latestFinishedImportTask: ImportTask | undefined;
  incompleteImportTask: ImportTask | undefined;
  isPending: boolean;
  onNavigateToTask: (taskId: ImportTask.Id) => void;
};

function formatExtractedCount(value: number | null | undefined): string {
  return typeof value === 'number' ? `${value}件` : '-';
}

function incompleteImportStatusLabel(status: ImportTask['status']): string {
  if (status === 'Requested') return '受付済み';
  if (status === 'Started') return '実行中';
  return status;
}

export function LatestImportSummarySection({
  latestFinishedImportTask,
  incompleteImportTask,
  isPending,
  onNavigateToTask,
}: LatestImportSummarySectionProps): ReactElement {
  const summary = latestFinishedImportTask?.resultJson?.summary;
  const finishedAt = latestFinishedImportTask?.finishedAt;

  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-col gap-1">
        <h2 className="text-sm font-semibold text-default">最新の取り込み</h2>
        <p className="text-xs text-muted-light">直近に完了した取り込みタスクで識別できた一意な顧客数・契約数です。</p>
      </div>
      <div className="flex flex-col gap-3 rounded-lg border border-default p-4">
        {isPending ? (
          <div className="flex justify-center py-6">
            <Loader />
          </div>
        ) : incompleteImportTask || (latestFinishedImportTask && summary) ? (
          <>
            {incompleteImportTask ? (
              <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
                <p className="text-default">取り込み中（{incompleteImportStatusLabel(incompleteImportTask.status)}）</p>
                <Button type="button" variant="quiet" size="sm" onClick={() => onNavigateToTask(incompleteImportTask.id)}>
                  取り込みタスクを開く
                </Button>
              </div>
            ) : null}
            {latestFinishedImportTask && summary ? (
              <>
                <dl className="grid gap-4 sm:grid-cols-2">
                  <div className="rounded-lg border border-default bg-surface p-4">
                    <dt className="text-xs font-medium text-muted-light">顧客数</dt>
                    <dd className="mt-2 text-2xl font-semibold tabular-nums text-primary">
                      {formatExtractedCount(summary.extractedCustomers)}
                    </dd>
                  </div>
                  <div className="rounded-lg border border-default bg-surface p-4">
                    <dt className="text-xs font-medium text-muted-light">契約数</dt>
                    <dd className="mt-2 text-2xl font-semibold tabular-nums text-notice">
                      {formatExtractedCount(summary.extractedContracts)}
                    </dd>
                  </div>
                </dl>
                <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
                  <p className="text-muted-light">取り込み完了: {finishedAt ? finishedAt.format('YYYY-MM-DD HH:mm') : '—'}</p>
                  <Button type="button" variant="quiet" size="sm" onClick={() => onNavigateToTask(latestFinishedImportTask.id)}>
                    取り込みタスクを開く
                  </Button>
                </div>
              </>
            ) : (
              <p className="text-sm text-muted-light">完了した取り込みはまだありません。</p>
            )}
          </>
        ) : (
          <p className="text-sm text-muted-light">まだ完了した取り込みがありません。</p>
        )}
      </div>
    </section>
  );
}
